/**
 * Video Progress Hybrid Service
 * Tries the backend API first, falls back to localStorage if unavailable.
 * Syncs localStorage data → API on startup.
 *
 * Exports the SAME interface as videoProgressService.js so that
 * hooks/components can swap imports with minimal changes.
 */

import * as localService from "./videoProgressService";
import * as apiService from "./videoProgressApiService";

export { DEFAULT_THRESHOLD_SECONDS } from "./videoProgressService";

/**
 * @typedef {import('./videoProgressService').VideoProgress} VideoProgress
 */

/** Track which users have already been synced this session. */
const _syncedUsers = new Set();

/**
 * Determine if an error is a transient/network failure (API unavailable).
 * @param {Error} error
 * @returns {boolean}
 */
function isApiUnavailable(error) {
  if (!error) return true;
  const msg = error.message || "";
  // Network failures, server errors (5xx), or CORS issues
  return (
    msg.includes("Failed to fetch") ||
    msg.includes("NetworkError") ||
    msg.includes("500") ||
    msg.includes("502") ||
    msg.includes("503") ||
    msg.includes("504") ||
    msg.includes("ERR_NETWORK") ||
    error.name === "TypeError" // fetch rejects with TypeError on network failure
  );
}

/**
 * Sync localStorage progress → API for a given user.
 * Runs once per session per user. Only sends entries the API doesn't have.
 * @param {string} userId
 * @param {Function} navigate
 * @returns {Promise<void>}
 */
export async function syncLocalToApi(userId, navigate) {
  if (!userId || _syncedUsers.has(userId)) return;
  _syncedUsers.add(userId);

  try {
    const localData = localService.getAllProgress(userId);
    if (!localData || Object.keys(localData).length === 0) return;

    const apiData = await apiService.getAllProgress(navigate);

    // Merge: for each local entry, if API is missing or local has more progress, include it
    const toSync = {};
    for (const [videoId, localEntry] of Object.entries(localData)) {
      const apiEntry = apiData[videoId];
      if (
        !apiEntry ||
        localEntry.secondsWatched > (apiEntry.secondsWatched || 0) ||
        localEntry.viewCount > (apiEntry.viewCount || 0)
      ) {
        toSync[videoId] = localEntry;
      }
    }

    if (Object.keys(toSync).length > 0) {
      await apiService.bulkUpsertProgress(toSync, navigate);
    }
  } catch {
    // Sync failed — will retry next session. Don't block the user.
  }
}

/**
 * Get progress for a single video.
 * API-first with localStorage fallback.
 * @param {string} userId
 * @param {string|number} videoId
 * @param {Function} [navigate] - Required for API calls
 * @returns {Promise<VideoProgress|null>}
 */
export async function getVideoProgress(userId, videoId, navigate) {
  if (!userId || videoId == null || videoId === '') return null;

  if (navigate) {
    try {
      const result = await apiService.getVideoProgress(videoId, navigate);
      return result;
    } catch (error) {
      if (!isApiUnavailable(error)) throw error;
    }
  }

  // Fallback to localStorage
  return localService.getVideoProgress(userId, videoId);
}

/**
 * Get progress for all videos.
 * API-first with localStorage fallback.
 * @param {string} userId
 * @param {Function} [navigate]
 * @returns {Promise<Object.<string, VideoProgress>>}
 */
export async function getAllProgress(userId, navigate) {
  if (!userId) return {};

  if (navigate) {
    try {
      const result = await apiService.getAllProgress(navigate);
      if (result && Object.keys(result).length > 0) return result;
    } catch (error) {
      if (!isApiUnavailable(error)) throw error;
    }
  }

  return localService.getAllProgress(userId);
}

/**
 * Update video progress.
 * Writes to API first, then always writes to localStorage as backup.
 * @param {string} userId
 * @param {string|number} videoId
 * @param {Object} options
 * @param {number} options.secondsWatched
 * @param {number} [options.videoDuration]
 * @param {boolean} [options.countNewView]
 * @param {Function} [navigate]
 * @returns {Promise<VideoProgress|null>}
 */
export async function updateVideoProgress(userId, videoId, options, navigate) {
  if (!userId || videoId == null || videoId === '') return null;

  // Always write to localStorage as a backup
  const localResult = localService.updateVideoProgress(userId, videoId, options);

  if (navigate) {
    try {
      const apiResult = await apiService.updateVideoProgress(videoId, options, navigate);
      return apiResult;
    } catch (error) {
      if (!isApiUnavailable(error)) throw error;
    }
  }

  return localResult;
}

/**
 * Get progress summary grouped by position.
 * Uses getAllProgress (API-first) then computes locally.
 * @param {string} userId
 * @param {Array<Object>} microconceptos
 * @param {Function} [navigate]
 * @returns {Promise<{ total: number, watched: number, byPosition: Object }>}
 */
export async function getProgressByPosition(userId, microconceptos, navigate) {
  const progress = await getAllProgress(userId, navigate);

  const byPosition = {};
  let totalWatched = 0;

  for (const mc of microconceptos) {
    const pos = mc.posicion || "Sin posición";
    if (!byPosition[pos]) {
      byPosition[pos] = { total: 0, watched: 0 };
    }
    byPosition[pos].total++;

    const vid = mc.id;
    if (vid != null && vid !== "") {
      const key = String(vid);
      if (progress[key]?.watched) {
        byPosition[pos].watched++;
        totalWatched++;
      }
    }
  }

  return {
    total: microconceptos.length,
    watched: totalWatched,
    byPosition,
  };
}

/**
 * Get count of watched videos.
 * @param {string} userId
 * @param {Function} [navigate]
 * @returns {Promise<number>}
 */
export async function getWatchedCount(userId, navigate) {
  const all = await getAllProgress(userId, navigate);
  return Object.values(all).filter((entry) => entry.watched).length;
}

/**
 * Reset progress for a single video.
 * Resets on API and localStorage.
 * @param {string} userId
 * @param {string|number} videoId
 * @param {Function} [navigate]
 * @returns {Promise<VideoProgress|null>}
 */
export async function resetVideoProgress(userId, videoId, navigate) {
  if (!userId || videoId == null || videoId === '') return null;

  // Always reset in localStorage
  const localResult = localService.resetVideoProgress(userId, videoId);

  if (navigate) {
    try {
      const apiResult = await apiService.resetVideoProgress(videoId, navigate);
      return apiResult;
    } catch (error) {
      if (!isApiUnavailable(error)) throw error;
    }
  }

  return localResult;
}

/**
 * Clear all progress for a user.
 * Clears localStorage immediately. API clear is best-effort.
 * @param {string} userId
 * @param {Function} [navigate]
 * @returns {Promise<void>}
 */
export async function clearProgress(userId, navigate) {
  localService.clearProgress(userId);
  // Note: no API endpoint for bulk delete yet — localStorage is cleared.
  // Backend team can add DELETE /videoProgress if needed.
}

/**
 * Estimate current localStorage storage usage (hybrid keeps local as backup).
 * @param {string} userId
 * @returns {number}
 */
export function getStorageSize(userId) {
  return localService.getStorageSize(userId);
}
